import { Injectable, inject } from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  from,
  lastValueFrom,
  Observable,
  of
} from "rxjs";
import { catchError, finalize, map, switchMap, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { sntLog } from "src/app/common/logger";
import { Mutex } from "async-mutex";
import {
  TaskExecutionPlan,
  TasksService,
  TaskType
} from "src/app/services/tasks.service";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { Box } from "src/app/modules/bootstrap/rocket-ui/backend/models/box";
import {
  LocalNetworkAccessQueryResult,
  LocalNetworkAccessService
} from "src/app/services/local-network-access.service";

export enum BoxStatus {
  ONLINE = "online",
  CHECKING = "checking",
  OFFLINE = "offline",
  DENIED = "denied"
}

export type OHIFViewerServerConfig = Record<
  string,
  Array<{
    name: string;
    wadoUriRoot: string;
    qidoRoot: string;
    wadoRoot: string;
    qidoSupportsIncludeField: boolean;
    imageRendering: string;
    thumbnailRendering: string;
    enableStudyLazyLoad: boolean;
    supportsFuzzyMatching: boolean;
    staticWado?: boolean;
  }>
>;

const logger = sntLog("BoxService");

@Injectable()
export class BoxService {
  boxStatus$: Observable<BoxStatus>;

  #boxAvailability$: BehaviorSubject<BoxStatus> =
    new BehaviorSubject<BoxStatus>(BoxStatus.OFFLINE);
  boxStatusLastCheck$: BehaviorSubject<Date | null> =
    new BehaviorSubject<Date | null>(null);

  #backgroundCheckIsRunning: boolean = false;

  readonly #mutex = new Mutex();

  readonly #bootstrapService = inject(BootstrapService);
  readonly #tasksService = inject(TasksService);
  readonly #localNetworkAccessService = inject(LocalNetworkAccessService);

  constructor() {
    this.#boxAvailability$
      .pipe(
        tap((boxStatus) => {
          logger.info(`box availability: "${boxStatus}"`);
          this.boxStatusLastCheck$.next(new Date());
        })
      )
      .subscribe();

    this.boxStatus$ = combineLatest([
      this.#boxAvailability$,
      this.#localNetworkAccessService.change$
    ]).pipe(
      map(([boxAvailibility, localNetworkAccessQueryResult]) => {
        switch (localNetworkAccessQueryResult) {
          case LocalNetworkAccessQueryResult.GRANTED:
          case LocalNetworkAccessQueryResult.NOT_FOUND: {
            if (!this.#backgroundCheckIsRunning) {
              this.startContinuousCheck();
            }

            return boxAvailibility;
          }
          case LocalNetworkAccessQueryResult.PROMPT: {
            return boxAvailibility;
          }
          case LocalNetworkAccessQueryResult.DENIED: {
            return BoxStatus.DENIED;
          }
        }
      })
    );
  }

  startContinuousCheck(): void {
    const currentBox = this.#bootstrapService.getCurrentBox();
    if (!currentBox) {
      logger.warn("current box not found");
      return;
    }

    this.#backgroundCheckIsRunning = true;
    logger.debug("continuous check started");

    this.isAvailable(currentBox);

    this.#tasksService.schedule({
      type: TaskType.RECURRING,
      interval: environment.snoots.box.checkInterval,
      plan: TaskExecutionPlan.WHEN_STABLE,
      fn: () => {
        this.isAvailable(currentBox);
      }
    });
  }

  async isOnline(): Promise<boolean> {
    await this.#mutex.acquire();
    const value = this.#boxAvailability$.value === "online";
    this.#mutex.release();
    return value;
  }

  lastValueIsOnline(): boolean {
    return this.#boxAvailability$.value === "online";
  }

  isNotNeverSharing(): boolean {
    const currentBox = this.#bootstrapService.getCurrentBox();
    return currentBox?.sharing !== "never";
  }

  async isCurrentBoxAvailable(): Promise<boolean> {
    return await this.isAvailable(this.#bootstrapService.getCurrentBox()!);
  }

  instantCheck(box: Box): Promise<boolean> {
    return this.isAvailable(box);
  }

  private async isAvailable(box: Box): Promise<boolean> {
    await this.#mutex.acquire();

    const url = new URL(
      environment.snoots.box.checkEndpointFn(
        box.address,
        environment.snoots.box.port
      )
    );

    url.searchParams.append("nocache", new Date().getTime().toString());
    url.searchParams.append("ngsw-bypass", "true");

    this.#boxAvailability$.next(BoxStatus.CHECKING);

    const abortController = new AbortController();
    const id = setTimeout(() => abortController.abort(), 10 * 1000);

    logger.debug(`checking at ${url.toString()}`);

    return await lastValueFrom(
      from(
        fetch(url.toString(), {
          method: "POST",
          signal: abortController.signal
        })
      ).pipe(
        switchMap((response) => {
          return from(response.json()).pipe(
            map((jsonResponse) => {
              return {
                status: response.status,
                json: jsonResponse
              };
            })
          );
        }),
        map(({ status, json }) => {
          if (status !== 200) {
            return false;
          }

          if (json.result == 0) {
            return true;
          }

          return false;
        }),
        map((status: boolean) => {
          this.#boxAvailability$.next(
            status ? BoxStatus.ONLINE : BoxStatus.OFFLINE
          );
          return true;
        }),
        catchError((error) => {
          this.#boxAvailability$.next(BoxStatus.OFFLINE);
          return of(false);
        }),
        finalize(() => {
          clearTimeout(id);
          this.#mutex.release();
        })
      )
    );
  }

  async getCurrentBox(): Promise<Box | null> {
    return null;
  }

  getPublicServerConfig(
    tenantId: string,
    storageId: string
  ): OHIFViewerServerConfig {
    return this.getViewerServerConfig({
      box: null,
      boxAvailable: false,
      tenantId,
      storageId
    });
  }

  getViewerServerConfig({
    box,
    boxAvailable,
    tenantId,
    storageId
  }: {
    box: Box | null;
    boxAvailable: boolean;
    tenantId: string;
    storageId: string;
  }): OHIFViewerServerConfig {
    if (boxAvailable) {
      return {
        dicomWeb: [
          {
            name: "DCM4CHEE",
            wadoUriRoot: `https://${box?.address}:8443/dcm4chee-arc/aets/DCM4CHEE/wado`,
            qidoRoot: `https://${box?.address}:8443/dcm4chee-arc/aets/DCM4CHEE/rs`,
            wadoRoot: `https://${box?.address}:8443/dcm4chee-arc/aets/DCM4CHEE/rs`,
            qidoSupportsIncludeField: true,
            imageRendering: "wadouri",
            thumbnailRendering: "wadouri",
            enableStudyLazyLoad: true,
            supportsFuzzyMatching: true
          }
        ]
      };
    } else {
      return {
        dicomWeb: [
          {
            name: "LocalStatic",
            wadoUriRoot: environment.snoots.viewer.storage.wadoUriRootFn({
              tenantId,
              storageId
            }),
            qidoRoot: environment.snoots.viewer.storage.qidoRootFn({
              tenantId,
              storageId
            }),
            wadoRoot: environment.snoots.viewer.storage.wadoRoot({
              tenantId,
              storageId
            }),
            qidoSupportsIncludeField: false,
            imageRendering: "wadors",
            thumbnailRendering: "thumbnail",
            enableStudyLazyLoad: true,
            supportsFuzzyMatching: false,
            staticWado: true
          }
        ]
      };
    }
  }
}
